import { useState, useEffect, useRef } from 'react';
import BackButton from '../components/BackButton';
import { useApp } from '../App';
import api from '../api';
import Loading from '../components/Loading';
import VoiceButton from '../components/VoiceButton';
import { useI18n } from '../i18n/index.jsx';

export default function Profile() {
  const { user, setUser, setPage } = useApp();
  const { t } = useI18n();
  const [form, setForm] = useState({});
  const [memberships, setMemberships] = useState([]);
  const [balances, setBalances] = useState([]);
  const [totalPln, setTotalPln] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  // OCR state
  const [ocrStep, setOcrStep] = useState('idle'); // idle|uploading|processing|preview|applied
  const [ocrPreview, setOcrPreview] = useState(null);
  const [ocrError, setOcrError] = useState('');
  const [ocrStepIdx, setOcrStepIdx] = useState(0);
  const fileRef = useRef(null);
  const OCR_STEPS = ['📤 Upload', '✨ Enhance', '🔍 OCR', '📋 MRZ', '✅ Fill'];
  // Voice dictation target
  const [voiceTarget, setVoiceTarget] = useState(null);

  useEffect(() => { loadProfile(); }, []);

  async function loadProfile() {
    try {
      const [me, mem, bal] = await Promise.all([
        api.get('/me'),
        api.get('/me/memberships').catch(() => ({ data: [] })),
        api.get('/balance/my').catch(() => ({ data: { balances: [], total_pln: 0 } })),
      ]);
      setForm(me.data);
      setMemberships(mem.data);
      setBalances(bal.data.balances || []);
      setTotalPln(bal.data.total_pln || 0);
    } catch { setForm(user); }
    setLoading(false);
  }

  function upd(k, v) { setForm({ ...form, [k]: v }); }

  async function save() {
    setSaving(true); setMsg('');
    try {
      const { data } = await api.put('/me', form);
      setUser(data);
      setMsg('✅ ' + t('profile.saved'));
    } catch (e) { setMsg('❌ ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  }

  async function submit() {
    try {
      const { data } = await api.post('/me/submit');
      setUser(data);
      setMsg('✅ ' + t('profile.submitted'));
    } catch (e) { setMsg('❌ ' + (e.response?.data?.detail || e.message)); }
  }

  // ── OCR Passport ──
  async function handlePassportUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrStep('uploading');
    setOcrError('');
    setOcrPreview(null);
    setOcrStepIdx(0);

    // Animate steps
    const animateSteps = async () => {
      for (let i = 0; i < OCR_STEPS.length - 1; i++) {
        await new Promise(r => setTimeout(r, 600));
        setOcrStepIdx(i + 1);
      }
    };
    setOcrStep('processing');
    const animPromise = animateSteps();

    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/documents/ocr-passport', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      await animPromise;
      if (data.fields) {
        setOcrStep('preview');
        setOcrPreview(data.fields);
      } else {
        setOcrStep('idle');
        setOcrError(t('profile.recognition_error'));
      }
    } catch (e) {
      await animPromise;
      setOcrStep('idle');
      const detail = e.userMessage || e.response?.data?.detail || e.message;
      if (detail.includes('blur') || detail.includes('quality')) {
        setOcrError('📷 ' + t('profile.blurry_photo'));
      } else {
        setOcrError('❌ ' + detail);
      }
    }
  }

  function applyOcr() {
    if (!ocrPreview) return;
    const mapping = {
      first_name: 'first_name', last_name: 'last_name',
      date_of_birth: 'date_of_birth', citizenship: 'citizenship',
      passport_number: 'passport_number', passport_expiry: 'passport_expiry',
    };
    const updated = { ...form };
    for (const [ocrKey, formKey] of Object.entries(mapping)) {
      if (ocrPreview[ocrKey]) updated[formKey] = ocrPreview[ocrKey];
    }
    setForm(updated);
    setOcrPreview(null);
    setOcrStep('applied');
    setMsg('✅ ' + t('profile.data_applied'));
  }

  // ── Voice dictation handler ──
  function handleVoice(text, error) {
    if (error || !text || !voiceTarget) return;
    upd(voiceTarget, text);
    setVoiceTarget(null);
  }

  if (loading) return <Loading />;

  const fields = [
    ['first_name', t('profile.first_name'), 'text'],
    ['last_name', t('profile.last_name'), 'text'],
    ['date_of_birth', t('profile.date_of_birth'), 'date'],
    ['citizenship', t('profile.citizenship'), 'text'],
    ['passport_number', t('profile.passport_number'), 'text'],
    ['passport_expiry', t('profile.passport_expiry'), 'date'],
    ['pesel', t('profile.pesel'), 'text'],
    ['phone', t('profile.phone'), 'tel'],
    ['email', t('profile.email'), 'email'],
    ['address_street', t('profile.address_street'), 'text'],
    ['address_city', t('profile.address_city'), 'text'],
    ['address_zip', t('profile.address_zip'), 'text'],
  ];

  const contractLabels = { umowa_zlecenia: 'Umowa zlecenia', umowa_o_dzielo: 'Umowa o dzieło', umowa_o_prace: 'Umowa o pracę' };

  // OCR Processing screen
  if (ocrStep === 'processing' || ocrStep === 'uploading') {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
        <h2>{t('profile.recognizing')}</h2>
        <p style={{ opacity: 0.6, marginBottom: 24 }}>{t('profile.recognizing_desc')}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
          {OCR_STEPS.map((step, i) => (
            <div key={i} style={{
              padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              background: i <= ocrStepIdx ? 'rgba(124,58,237,0.4)' : 'rgba(255,255,255,0.05)',
              color: i <= ocrStepIdx ? '#a78bfa' : 'rgba(255,255,255,0.3)',
              border: `1px solid ${i <= ocrStepIdx ? 'rgba(124,58,237,0.4)' : 'transparent'}`,
              transition: 'all 0.4s ease',
            }}>{step}</div>
          ))}
        </div>
        <div style={{ width: '100%', height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 2,
            background: 'linear-gradient(90deg, #7c3aed, #a78bfa)',
            width: `${((ocrStepIdx + 1) / OCR_STEPS.length) * 100}%`,
            transition: 'width 0.6s ease',
          }} />
        </div>
      </div>
    );
  }

  // OCR Preview screen
  if (ocrStep === 'preview' && ocrPreview) {
    const fieldLabels = {
      first_name: t('profile.first_name'), last_name: t('profile.last_name'),
      date_of_birth: t('profile.date_of_birth'), citizenship: t('profile.citizenship'),
      passport_number: t('profile.passport_number'), passport_expiry: t('profile.passport_expiry'),
    };
    return (
      <div className="page">
      <BackButton />
        <h1>📷 {t('profile.check_data')}</h1>
        <p style={{ opacity: 0.7, marginBottom: 16 }}>{t('profile.data_recognized')}</p>
        <div className="detail-card" style={{ marginBottom: 16 }}>
          {Object.entries(ocrPreview).map(([k, v]) => v && (
            <div key={k} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, opacity: 0.5, marginBottom: 3 }}>{fieldLabels[k] || k.replace(/_/g, ' ')}</div>
              <input
                className="input"
                defaultValue={v}
                onChange={(e) => setOcrPreview({ ...ocrPreview, [k]: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>
          ))}
        </div>
        <div className="actions">
          <button className="btn btn-primary" onClick={applyOcr}>✅ {t('profile.apply_data')}</button>
          <button className="btn btn-secondary" onClick={() => setOcrStep('idle')}>✏️ {t('profile.enter_manually')}</button>
          <button className="btn btn-secondary" onClick={() => { setOcrStep('idle'); fileRef.current?.click(); }}>📷 {t('profile.other_photo')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <BackButton />
      <h1>👤 {t('profile.title')}</h1>
      <div className={`status-badge status-${form.profile_status || 'draft'}`} style={{ display: 'inline-block', marginBottom: 12 }}>
        {form.profile_status === 'verified' ? '✅ ' + t('profile.status_verified') :
         form.profile_status === 'pending' ? '⏳ ' + t('profile.status_pending') : '📝 ' + t('profile.status_draft')}
      </div>

      {/* Guide Button - Big and Prominent */}
      <div
        onClick={() => setPage('guide')}
        style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #10b981 100%)',
          borderRadius: 16,
          padding: 20,
          marginBottom: 16,
          cursor: 'pointer',
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(245,158,11,0.4)',
          animation: 'pulse 2s infinite',
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 8 }}>💰📚💎</div>
        <div style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 4 }}>
          {t('profile.guide_title')}
        </div>
        <div style={{ fontSize: 14, opacity: 0.9 }}>
          {t('profile.guide_desc')}
        </div>
        <div style={{
          marginTop: 12,
          padding: '8px 16px',
          background: 'rgba(255,255,255,0.2)',
          borderRadius: 8,
          display: 'inline-block',
          fontWeight: 'bold',
        }}>
          👆 {t('profile.guide_cta')}
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
      `}</style>

      {/* Referral Link */}
      {user?.referral_code && (
        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 12,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 8 }}>🔗 {t('profile.referral_title')}</div>
          <div style={{ 
            background: 'rgba(0,0,0,0.2)', 
            padding: 8, 
            borderRadius: 6, 
            fontSize: 12,
            wordBreak: 'break-all',
            marginBottom: 8,
          }}>
            https://t.me/WORKIFYPL_bot?start=ref_{user.referral_code}
          </div>
          <button 
            className="btn btn-small"
            style={{ background: 'rgba(255,255,255,0.2)' }}
            onClick={() => {
              navigator.clipboard.writeText(`https://t.me/WORKIFYPL_bot?start=ref_${user.referral_code}`);
              setMsg('✅ ' + t('profile.referral_copied'));
            }}
          >
            📋 {t('profile.referral_copy')}
          </button>
          <div style={{ fontSize: 11, opacity: 0.8, marginTop: 8 }}>
            {t('profile.referral_invite')}
          </div>
        </div>
      )}

      {/* Balance */}
      <div style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 12,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 12 }}>💰 {t('profile.balance_title')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
          {balances.map(b => (
            <div key={b.currency} style={{
              background: 'rgba(255,255,255,0.1)',
              borderRadius: 8,
              padding: 8,
              textAlign: 'center',
            }}>
              <div style={{ fontWeight: 'bold', fontSize: 16 }}>{b.amount.toFixed(2)}</div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>{b.currency}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 13, textAlign: 'center', opacity: 0.9 }}>
          {t('profile.balance_total')}: <b>{totalPln.toFixed(2)} PLN</b>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-small" style={{ flex: 1, background: 'rgba(255,255,255,0.2)' }} onClick={() => setPage('top_up')}>
            💰 {t('profile.balance_top_up')}
          </button>
          <button className="btn btn-small" style={{ flex: 1, background: 'rgba(255,255,255,0.2)' }} onClick={() => setPage('exchange')}>
            💱 {t('profile.balance_exchange')}
          </button>
        </div>
      </div>

      {/* Company & Contract */}
      {memberships.length > 0 ? (
        <div className="detail-card" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: '0 0 8px' }}>🏢 {t('profile.company_contract')}</h3>
          {memberships.map(m => (
            <div key={m.id} style={{ marginBottom: 6 }}>
              <b>{m.company?.short_name || m.company?.name || `${t('common.company')} #${m.company_id}`}</b>
              <br/>{m.position || '—'} • {contractLabels[m.contract_type] || m.contract_type}
              {m.kup_50 && ' • 50% KUP'}
              <span className={`status-badge status-${m.status}`} style={{ marginLeft: 6 }}>{m.status}</span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ marginBottom: 12 }}>
          <div className="banner banner-warning" style={{ marginBottom: 8 }}>
            🏢 {t('profile.no_company')}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={() => setPage('company_select')}>🏢 {t('profile.choose_company')}</button>
            <button className="btn btn-secondary" onClick={() => setPage('ai_lawyer')}>💬 {t('profile.contact_us')}</button>
          </div>
        </div>
      )}

      {/* Passport Scan */}
      <div onClick={() => ocrStep === 'idle' || ocrStep === 'applied' ? fileRef.current?.click() : null}
        style={{
          background: ocrStep === 'applied'
            ? 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.05))'
            : 'linear-gradient(135deg, var(--card), rgba(124,58,237,0.15))',
          border: `2px dashed ${ocrStep === 'applied' ? '#10b981' : 'var(--accent2)'}`,
          borderRadius: 16, padding: 20, textAlign: 'center',
          cursor: ocrStep === 'idle' || ocrStep === 'applied' ? 'pointer' : 'default',
          marginBottom: 16, transition: 'all 0.3s ease',
        }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>
          {ocrStep === 'applied' ? '✅' : '📷'}
        </div>
        <div style={{ fontWeight: 700, fontSize: 16 }}>
          {ocrStep === 'applied' ? t('profile.passport_applied') : t('profile.scan_passport')}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
          {ocrStep === 'applied' ? t('profile.click_to_update') : t('profile.scan_passport_desc')}
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" capture="environment"
        onChange={handlePassportUpload} style={{ display: 'none' }} />
      {ocrError && <div className="banner banner-warning" style={{ marginBottom: 12 }}>{ocrError}
        <button className="btn btn-small" onClick={() => fileRef.current?.click()} style={{ marginLeft: 8 }}>📷 {t('common.retry')}</button>
      </div>}

      {/* Voice dictation indicator */}
      {voiceTarget && (
        <div className="banner banner-info" style={{ marginBottom: 8 }}>
          🎙️ {t('profile.dictate_field')}: <b>{fields.find(f => f[0] === voiceTarget)?.[1] || voiceTarget}</b>
          <VoiceButton onResult={handleVoice} style={{ marginLeft: 8, padding: '4px 10px', borderRadius: 8, fontSize: 14 }} />
          <button className="btn btn-small" onClick={() => setVoiceTarget(null)} style={{ marginLeft: 4 }}>✕</button>
        </div>
      )}

      {/* Form fields with voice */}
      <div className="form">
        {fields.map(([k, label, type]) => (
          <div key={k} className="field">
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {label}
              {type !== 'date' && (
                <button onClick={() => setVoiceTarget(k)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, opacity: voiceTarget === k ? 1 : 0.5 }}>
                  🎙️
                </button>
              )}
            </label>
            <input type={type} value={form[k] || ''} onChange={e => upd(k, e.target.value)} />
          </div>
        ))}
      </div>
      {msg && <div className="message">{msg}</div>}
      <div className="actions">
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? '⏳...' : '💾 ' + t('profile.save_btn')}
        </button>
        {form.profile_status === 'draft' && (
          <button className="btn btn-secondary" onClick={submit}>📤 {t('profile.submit_review')}</button>
        )}
      </div>
    </div>
  );
}
